import type { WhenDatePart, WhenDurationPreset, WhenSelection } from "@ui/components/SearchBar";

// Extra calendar months a duration adds beyond the start month. Sub-month trips
// stay within the start month; only multi-month trips widen the climate window.
// Counted in whole months (not days) so a late-day start can't roll an extra
// month (e.g. Jan 31 + "1 month" stays Jan, not Jan–Mar). Climate is
// yearly-cyclical, so only the spanned month numbers matter.
const DURATION_EXTRA_MONTHS: Record<WhenDurationPreset, number> = {
  overnight: 0,
  "3-days": 0,
  "1-week": 0,
  "2-weeks": 0,
  "1-month": 0,
  "3-months": 2,
};

const MAX_MONTHS = 12;

// Absolute month index (year*12 + month-1) for a concrete part; null for a
// duration, which is only meaningful for the end (relative to the start).
function partToMonthIndex(part: WhenDatePart): number | null {
  if (part.kind === "month") return part.year * 12 + (part.month - 1);
  if (part.kind === "date") return part.value.year * 12 + (part.value.month - 1);
  return null;
}

/**
 * Resolve a When selection into the set of calendar months (1..12) the trip
 * spans, so destinations climate scoring can rank by the average weather across
 * the whole window. Always returns at least one month.
 *
 * A selection with no start falls back to `defaultMonth`, which the caller
 * reads off the clock: browsing without dates is asking where to go now, and
 * the answer in November is not the answer in July. A year-round average would
 * be nobody's actual trip, and it would flatten the four seasonal factors that
 * should be deciding the question.
 *
 * The clock stays in the caller so this function is pure, and so the fallback
 * is a value the disclosure panel can name back to the reader rather than a
 * hidden default.
 */
export function whenToMonths(when: WhenSelection, defaultMonth: number): [number, ...number[]] {
  const startIndex = when.start && partToMonthIndex(when.start);
  if (startIndex === null || startIndex === undefined) return [defaultMonth];

  let endIndex = startIndex;
  if (when.end?.kind === "duration") {
    endIndex = startIndex + DURATION_EXTRA_MONTHS[when.end.preset];
  } else if (when.end) {
    endIndex = partToMonthIndex(when.end) ?? startIndex;
  }
  // Custom end dates can land before the start; clamp so the range is never empty.
  if (endIndex < startIndex) endIndex = startIndex;

  const months: number[] = [];
  const seen = new Set<number>();
  for (let index = startIndex; index <= endIndex && months.length < MAX_MONTHS; index++) {
    const month = (((index % 12) + 12) % 12) + 1;
    if (!seen.has(month)) {
      seen.add(month);
      months.push(month);
    }
  }
  // The loop always runs at least once (endIndex >= startIndex), so months is non-empty.
  return months as [number, ...number[]];
}
